/**
 * Public subscription-plan catalogue, readable during SSR.
 *
 * `/subscription` used to fetch plans in the browser after hydration, so the
 * server-rendered HTML was a skeleton and the plan cards became the LCP element
 * whenever the client bundle finally resolved. Plans are public catalogue data
 * (anon SELECT policy + grant), so they can be read on the server with the
 * publishable key and shipped inside the first paint.
 */
import { createServerFn } from "@tanstack/react-start";
import { memoPublic } from "@/lib/serverMemo";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

export interface PublicSubscriptionPlan {
  id: string;
  name: string;
  price: number;
  discounted_price: number | null;
  duration_days: number;
  /** True for the one-time ₹99 all-access pass (see src/lib/introPass.ts). */
  is_intro_pass?: boolean | null;
}

/** Temporary Razorpay smoke-test plans are admin-only; never SSR them. */
const ADMIN_TEST_RE = /^\[ADMIN TEST\]/i;

export const getPublicSubscriptionPlans = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicSubscriptionPlan[]> =>
    (await memoPublic("public_plans", 60_000, 2_500, async (): Promise<PublicSubscriptionPlan[]> => {
    // The build-time prerender has no server env; without the public VITE_
    // fallback the saved "/" and "/subscription" pages shipped with no prices.
    const url = process.env["SUPABASE_URL"] || import.meta.env["VITE_SUPABASE_URL"];
    const key =
      process.env["SUPABASE_PUBLISHABLE_KEY"] || import.meta.env["VITE_SUPABASE_PUBLISHABLE_KEY"];
    if (!url || !key) return [];

    const supabasePublic = createClient<Database>(url, key, {
      auth: { persistSession: false },
      global: {
        // sb_ publishable keys are opaque, not JWTs — send them as `apikey` only.
        fetch: (input, init) => {
          const headers = new Headers(init?.headers);
          if (key.startsWith("sb_") && headers.get("Authorization") === `Bearer ${key}`) {
            headers.delete("Authorization");
          }
          headers.set("apikey", key);
          return fetch(input, { ...init, headers });
        },
      },
    });

    const { data, error } = await supabasePublic
      .from("subscription_plans")
      .select("id, name, price, discounted_price, duration_days, is_intro_pass")
      .order("price", { ascending: false })
      .order("duration_days", { ascending: false });

    if (error || !data) throw new Error("plans unavailable");
    return (data as PublicSubscriptionPlan[]).filter((p) => !ADMIN_TEST_RE.test(p.name));
  })) ?? [],
);
